<template>
  <div class="w-full">
    <!-- Mobile: stack main then a row of two -->
    <div class="block md:hidden">
      <div class="mb-2 aspect-[3/3] overflow-hidden rounded-3xl">
        <div class="relative h-full w-full bg-purple-950/5">
          <div
            v-if="!isLoaded(0)"
            class="absolute inset-0 animate-pulse bg-purple-950/10"
          ></div>
          <img
            :ref="(el) => setImageRef(0, el)"
            :src="main"
            :alt="imageAlt(0)"
            class="h-full w-full object-cover transition-opacity duration-300"
            :class="isLoaded(0) ? 'opacity-100' : 'opacity-0'"
            loading="eager"
            decoding="async"
            fetchpriority="high"
            @load="onImageLoad(0)"
            @error="onImageError(0)"
          />
        </div>
      </div>
      <div class="flex gap-2">
        <div class="aspect-[3/3] flex-1 overflow-hidden rounded-3xl">
          <div class="relative h-full w-full bg-purple-950/5">
            <div
              v-if="!isLoaded(1)"
              class="absolute inset-0 animate-pulse bg-purple-950/10"
            ></div>
            <img
              :ref="(el) => setImageRef(1, el)"
              :src="secondary(0)"
              :alt="imageAlt(1)"
              class="h-full w-full object-cover transition-opacity duration-300"
              :class="isLoaded(1) ? 'opacity-100' : 'opacity-0'"
              loading="lazy"
              decoding="async"
              fetchpriority="low"
              @load="onImageLoad(1)"
              @error="onImageError(1)"
            />
          </div>
        </div>
        <div class="aspect-[3/3] flex-1 overflow-hidden rounded-3xl">
          <div class="relative h-full w-full bg-purple-950/5">
            <div
              v-if="!isLoaded(2)"
              class="absolute inset-0 animate-pulse bg-purple-950/10"
            ></div>
            <img
              :ref="(el) => setImageRef(2, el)"
              :src="secondary(1)"
              :alt="imageAlt(2)"
              class="h-full w-full object-cover transition-opacity duration-300"
              :class="isLoaded(2) ? 'opacity-100' : 'opacity-0'"
              loading="lazy"
              decoding="async"
              fetchpriority="low"
              @load="onImageLoad(2)"
              @error="onImageError(2)"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Tablet+ : main left (2 columns) and two stacked on right -->
    <div class="hidden gap-2 md:grid md:grid-cols-3">
      <div class="aspect-[3/3] overflow-hidden rounded-3xl md:col-span-2">
        <div class="relative h-full w-full bg-purple-950/5">
          <div
            v-if="!isLoaded(0)"
            class="absolute inset-0 animate-pulse bg-purple-950/10"
          ></div>
          <img
            :ref="(el) => setImageRef(0, el)"
            :src="main"
            :alt="imageAlt(0)"
            class="h-full w-full object-cover transition-opacity duration-300"
            :class="isLoaded(0) ? 'opacity-100' : 'opacity-0'"
            loading="eager"
            decoding="async"
            fetchpriority="high"
            @load="onImageLoad(0)"
            @error="onImageError(0)"
          />
        </div>
      </div>
      <div class="flex flex-col gap-2">
        <div class="aspect-[3/3] overflow-hidden rounded-3xl">
          <div class="relative h-full w-full bg-purple-950/5">
            <div
              v-if="!isLoaded(1)"
              class="absolute inset-0 animate-pulse bg-purple-950/10"
            ></div>
            <img
              :ref="(el) => setImageRef(1, el)"
              :src="secondary(0)"
              :alt="imageAlt(1)"
              class="h-full w-full object-cover transition-opacity duration-300"
              :class="isLoaded(1) ? 'opacity-100' : 'opacity-0'"
              loading="lazy"
              decoding="async"
              fetchpriority="low"
              @load="onImageLoad(1)"
              @error="onImageError(1)"
            />
          </div>
        </div>
        <div class="aspect-[3/3] overflow-hidden rounded-3xl">
          <div class="relative h-full w-full bg-purple-950/5">
            <div
              v-if="!isLoaded(2)"
              class="absolute inset-0 animate-pulse bg-purple-950/10"
            ></div>
            <img
              :ref="(el) => setImageRef(2, el)"
              :src="secondary(1)"
              :alt="imageAlt(2)"
              class="h-full w-full object-cover transition-opacity duration-300"
              :class="isLoaded(2) ? 'opacity-100' : 'opacity-0'"
              loading="lazy"
              decoding="async"
              fetchpriority="low"
              @load="onImageLoad(2)"
              @error="onImageError(2)"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from "vue";
import type { ComponentPublicInstance } from "vue";

const props = withDefaults(
  defineProps<{
    images?: string[];
    altPrefix?: string;
    fallbackImage?: string;
  }>(),
  {
    images: () => [],
    altPrefix: "Gallery",
    fallbackImage: "/images/placeholder-soft-neutral-generic.svg",
  },
);

const fallback = computed(
  () =>
    props.fallbackImage.trim() ||
    "/images/placeholder-soft-neutral-generic.svg",
);
const requestedSlots = computed(() =>
  Array.from(
    { length: 3 },
    (_, index) => props.images[index]?.trim() || fallback.value,
  ),
);
const slots = ref<string[]>([]);
const main = computed(() => slots.value[0] ?? fallback.value);
const loadedState = ref([false, false, false]);
const imageRefs = ref<Record<number, HTMLImageElement[]>>({
  0: [],
  1: [],
  2: [],
});

watch(
  requestedSlots,
  async (next) => {
    slots.value = next;
    imageRefs.value = { 0: [], 1: [], 2: [] };
    loadedState.value = [false, false, false];
    await nextTick();
    syncLoadedFromDom();
  },
  { immediate: true },
);

onMounted(() => {
  syncLoadedFromDom();
});

function secondary(i: number) {
  return slots.value[i + 1] ?? fallback.value;
}

function onImageLoad(idx: number) {
  loadedState.value[idx] = true;
}

function onImageError(idx: number) {
  if (slots.value[idx] !== fallback.value) {
    loadedState.value[idx] = false;
    slots.value[idx] = fallback.value;
    return;
  }

  loadedState.value[idx] = true;
}

function imageAlt(idx: number) {
  return `${props.altPrefix} image ${idx + 1}`;
}

function isLoaded(idx: number) {
  return loadedState.value[idx] === true;
}

function setImageRef(
  idx: number,
  el: Element | ComponentPublicInstance | null,
) {
  if (!(el instanceof HTMLImageElement)) return;
  const existing = imageRefs.value[idx] ?? [];
  if (!existing.includes(el)) {
    imageRefs.value[idx] = [...existing, el];
  }
}

function syncLoadedFromDom() {
  slots.value.forEach((_, idx) => {
    const refs = imageRefs.value[idx] ?? [];
    if (refs.some((img) => img.complete && img.naturalWidth > 0)) {
      loadedState.value[idx] = true;
    } else if (refs.some((img) => img.complete)) {
      onImageError(idx);
    }
  });
}
</script>
